import React, { useCallback, useState, useEffect, useRef } from 'react';
import { DatePicker, Form, Input, Button, Tabs, Modal } from 'antd';
import { getRangeDate, setRangeDate, clearRangeDate } from 'utils/dateRangeStorage';
import { FormProps } from 'rc-field-form';
import 'styles/consolidate-revenue.scss';

import { ExportConsolidateRevenue } from 'components/common/export-consolidate-revenue';
import RoomTypeTab from 'components/modal/consolidate-components/room-type-consolidate-revenue';
import SourceTab from 'components/modal/consolidate-components/source-consolidate-revenue';
import NationalityTab from 'components/modal/consolidate-components/nationality-consolidate-revenue';
import CampaignTab from 'components/modal/consolidate-components/campaign-consolidate-revenue';

import { generateExportFileName, getEarliestValidDate, toUtcTime } from 'utils';

import set from 'lodash/set';
import dayjs, { Dayjs } from 'dayjs';

import { useTranslation } from 'react-i18next';
import { ExportPuReport } from 'components/modal/consolidate-components/expor-pu-report';
import { DownloadOutlined } from '@ant-design/icons';
import useCommon from 'stores/useCommon';
import IBButton from 'helper/permission.action';
import { PermissionActionTypes } from 'helper/type/action.type';
import useModal from 'stores/useModal';
import { useSelectMultiBranches } from 'hooks/useSelectMultiBranches';
import SelectMultiBranchesModal from 'components/common/select-multi-branches-modal';

const INITIAL_RANGE_DATE = 14;
const initStartDate = dayjs().startOf('day');

function ConsolidateRevenue() {
  const { isOpenSourceBranchesModal, setIsOpenSourceBranchesModal } = useModal();
  const { currentBranch } = useCommon();
  const { t } = useTranslation();

  const [form] = Form.useForm();
  const [keyTab, setKeyTab] = useState<string>('room_type');
  const [dataSource, setDataSource] = useState<any[]>();
  const isFirstRender = useRef(true);

  const [isExportPuModalOpen, setIsExportPuModalOpen] = useState(false);

  const multiBranches = useSelectMultiBranches(PermissionActionTypes.SOURCE_REVENUE_BRANCHES);
  const {
    selectedSourceBranches,
    setSelectedSourceBranches,
    setAppliedSourceBranches,
    hasSourceBranchesPermission,
    hasAppliedSourceBranches,
    setHasAppliedSourceBranches,
    selectedBranchesData,
    getBranchIdsString
  } = multiBranches;

  const onTabClick = (key: string) => {
    setKeyTab(key);
  };

  const [filterOptions, setFilterOptions] = useState<any>(() => {
    const savedRange = getRangeDate();
    if (savedRange) {
      const startDate = dayjs(savedRange.startDate);
      return {
        start_date: toUtcTime(startDate.add(1, 'day'), 'YYYY-MM-DD'),
        period: savedRange.period
      };
    }
    return {
      start_date: toUtcTime(initStartDate.add(1, 'day'), 'YYYY-MM-DD'),
      period: INITIAL_RANGE_DATE
    };
  });

  const onFinish: FormProps['onFinish'] = async (objValue: any) => {
    const truthyValues: any = Object.keys(objValue)
      .filter(key => Boolean(objValue[key]))
      .reduce((cur, next) => {
        return {
          ...cur,
          [next]: objValue[next]
        };
      }, {});

    const { rangeDate, period, ...restFilterOptions } = truthyValues;
    if (rangeDate) {
      const startDate: Dayjs = rangeDate[0];
      const endDate: Dayjs = rangeDate[1];

      if (startDate && endDate) {
        const dayCount = endDate.diff(startDate, 'day');
        set(restFilterOptions, 'period', dayCount); // Fixed: add 1 to include end date
        set(restFilterOptions, 'start_date', toUtcTime(startDate.add(1, 'day'), 'YYYY-MM-DD'));
      }
    }

    set(restFilterOptions, 'period', period || INITIAL_RANGE_DATE);

    setFilterOptions(restFilterOptions);

    if (hasSourceBranchesPermission) {
      setHasAppliedSourceBranches(false);
    }
  };

  const handleRangeDateChange = (dates: any) => {
    if (dates && dates.length === 2) {
      const [start, end] = dates;
      const dayCount = end.diff(start, 'day');
      form.setFieldsValue({ period: dayCount + 1 }); // Fixed: add 1 to include end date
      setRangeDate(start, end, dayCount + 1);
    }
  };

  const handlePeriodChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const period = parseInt(e.target.value);
    if (!isNaN(period) && period > 0) {
      const rangeDate = form.getFieldValue('rangeDate');
      if (rangeDate && rangeDate[0]) {
        const startDate = dayjs(rangeDate[0]);
        const endDate = startDate.add(period - 1, 'day'); // Fixed: subtract 1 for correct range
        form.setFieldsValue({ rangeDate: [startDate, endDate] });
        setRangeDate(startDate, endDate, period + 1);
      }
    }
  };

  const fromDate = getEarliestValidDate(filterOptions.start_date);

  const toDate = filterOptions.start_date
    ? dayjs(filterOptions.start_date)
        .add(filterOptions.period, 'day')
        .subtract(1, 'day')
        .format('DD-MM-YYYY')
    : undefined;

  const handleSetDataSource = useCallback((data: any[]) => {
    if (!data.length) return; // if data is empty, don't set the data
    setDataSource(data);
  }, []);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;

      const savedRange = getRangeDate();
      if (savedRange) {
        const startDate = dayjs(savedRange.startDate);
        const endDate = dayjs(savedRange.endDate);
        form.setFieldsValue({
          rangeDate: [startDate, endDate],
          period: savedRange.period
        });
      } else {
        const endDate = initStartDate.add(INITIAL_RANGE_DATE - 1, 'day');
        form.setFieldsValue({
          rangeDate: [initStartDate, endDate],
          period: INITIAL_RANGE_DATE
        });
      }
    }
  }, [form]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      clearRangeDate();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  return (
    <div className="pms-consolidate-revenue">
      <div className="pms-consolidate-revenue__timetable">
        <Form form={form} onFinish={onFinish} autoComplete="off">
          <div className="pms-consolidate-revenue__filter">
            <div className="pms-consolidate-revenue__filter__left">
              <Form.Item name="rangeDate">
                <DatePicker.RangePicker
                  className="w-full"
                  placeholder={[t('common.time.fromDate'), t('common.time.toDate')]}
                  format="DD-MM-YYYY"
                  onChange={handleRangeDateChange}
                  allowClear={false}
                />
              </Form.Item>
              <Form.Item name="period">
                <Input placeholder={t('common.time.totalDays')} onChange={handlePeriodChange} />
              </Form.Item>
              <Form.Item>
                <Button htmlType="submit" className="ant-btn-secondary btn-submit">
                  {t('common.actions.submitButton')}
                </Button>
              </Form.Item>
              <Form.Item>
                <IBButton
                  key="ok"
                  permission={PermissionActionTypes.SOURCE_REVENUE_BRANCHES}
                  className="ant-btn-secondary"
                  onClick={() => setIsOpenSourceBranchesModal(true)}
                >
                  {t('managePage.consolidateRevenueReportPage.actions.sourceRevenueBranches')}
                </IBButton>
              </Form.Item>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              {/* Export PU button */}
              <Button icon={<DownloadOutlined />} onClick={() => setIsExportPuModalOpen(true)}>
                {t('common.actions.exportPU')}
              </Button>
              <ExportConsolidateRevenue
                fileName={generateExportFileName('consolidate-revenue')}
                fromDate={fromDate}
                t={t}
                toDate={toDate}
                dataToExport={dataSource}
                typeOfResponse={keyTab}
                hasSourceBranchesPermission={hasSourceBranchesPermission}
                selectedBranchesData={selectedBranchesData}
              />
            </div>
          </div>
        </Form>
        <div className="pms-consolidate-revenue__table">
          <Tabs type="card" activeKey={keyTab} onTabClick={onTabClick} defaultActiveKey="1">
            <Tabs.TabPane
              tab={t('managePage.consolidateRevenueReportPage.common.titleRoomType')}
              key="room_type"
            >
              <RoomTypeTab
                filterOptions={{ ...filterOptions, product_id: undefined }}
                isEnable={keyTab === 'room_type'}
                type_of_response={keyTab}
                setDataSource={handleSetDataSource}
              />
            </Tabs.TabPane>
            <Tabs.TabPane
              tab={t('managePage.consolidateRevenueReportPage.common.titleSource')}
              key="source"
            >
              <SourceTab
                filterOptions={{ ...filterOptions, product_id: undefined }}
                isEnable={
                  keyTab === 'source' &&
                  (hasAppliedSourceBranches || !hasSourceBranchesPermission || !!currentBranch?.id)
                }
                setDataSource={handleSetDataSource}
                branch_ids={getBranchIdsString()}
              />
            </Tabs.TabPane>
            <Tabs.TabPane
              tab={t('managePage.consolidateRevenueReportPage.common.titleCampaign')}
              key="campaign"
            >
              <CampaignTab
                filterOptions={{ ...filterOptions, product_id: undefined }}
                isEnable={keyTab == 'campaign'}
                setDataSource={handleSetDataSource}
              />
            </Tabs.TabPane>
            <Tabs.TabPane
              tab={t('managePage.consolidateRevenueReportPage.common.titleNationality')}
              key="nationality"
            >
              <NationalityTab
                filterOptions={{ ...filterOptions, product_id: undefined }}
                isEnable={keyTab === 'nationality'}
                type_of_response={keyTab}
                setDataSource={handleSetDataSource}
              />
            </Tabs.TabPane>
          </Tabs>
        </div>
      </div>
      {/* Export PU Modal */}
      <Modal
        open={isExportPuModalOpen}
        onCancel={() => setIsExportPuModalOpen(false)}
        footer={null}
        title="Export PU Report"
        destroyOnClose
        width={450}
      >
        <ExportPuReport currentBranch={currentBranch} />
      </Modal>

      <SelectMultiBranchesModal
        open={isOpenSourceBranchesModal}
        onCancel={() => setIsOpenSourceBranchesModal(false)}
        onOk={() => {
          setAppliedSourceBranches(selectedSourceBranches);
          setHasAppliedSourceBranches(true);
          setIsOpenSourceBranchesModal(false);
        }}
        selectedSourceBranches={selectedSourceBranches}
        setSelectedSourceBranches={setSelectedSourceBranches}
      />
    </div>
  );
}

export default ConsolidateRevenue;
